import React, { useEffect, useState, useCallback } from 'react';
import LogoutIcon from '@mui/icons-material/Logout';
import { Edit as EditIcon, VerifiedUser } from '@mui/icons-material';
import {
  Box,
  Button,
  Typography,
  Avatar,
  Alert,
  Grid,
  Paper,
  useTheme,
  useMediaQuery,
  IconButton,
  Backdrop,
  CircularProgress,
  Divider,
  Fade,
  Zoom,
  Grow,
  keyframes
} from '@mui/material';
import { getUserId, isEmailVerified } from '../utils/auth';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../config';
import { useTranslation } from '../admin/hooks/useTranslation';

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const ProfilePage = () => {
  const { t } = useTranslation();
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);

  const navigate = useNavigate();
  const { logout, userInfo, refreshUserInfo } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchPhoto = useCallback(() => {
    const userId = getUserId();
    if (!userId || !userInfo?.profilePhoto) return;
    const url = `${BASE_URL}/object-store/img/get/${userId}/SMALL/${userInfo.profilePhoto}`;
    setPhotoUrl(url);
  }, [userInfo, BASE_URL]);

  useEffect(() => {
    refreshUserInfo?.();
  }, []);

  useEffect(() => {
    const initialize = async () => {
      fetchPhoto();
      setFirstName(userInfo?.firstName || null);

      const payload = isEmailVerified();
      if (typeof payload?.email_verified === 'boolean') {
        setEmailVerified(payload.email_verified);
      } else {
        setEmailVerified(null);
      }

      setLoading(false);
    };

    if (userInfo) {
      initialize();
    }
  }, [userInfo, fetchPhoto]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <>
      <Grid container justifyContent="center" sx={{ mt: isMobile ? 2 : 6, px: 2 }}>
        <Grid size={{ xs: 12, sm: 8, md: 6, lg: 4 }}>
          <Fade in={true} timeout={500}>
            <Paper elevation={3} sx={{
              p: isMobile ? 2 : 4,
              borderRadius: 4,
              position: 'relative'
            }}>
              <Box textAlign="center">
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  {firstName
                    ? t('profile.welcomeUser', 'Welcome, {firstName}').replace('{firstName}', firstName)
                    : t('profile.welcome', 'Welcome')}
                </Typography>

                {/* Profile Photo */}
                <Box
                  position="relative"
                  sx={{
                    display: 'inline-block',
                    mt: 2,
                    mb: 3,
                    alignSelf: 'center',
                    '&:hover .edit-icon': {
                      opacity: 1,
                    },
                  }}
                >
                  {photoUrl ? (
                    <Avatar
                      src={photoUrl}
                      alt={t('profile.profilePhotoAlt', 'Profile Photo')}
                      sx={{
                        width: isMobile ? 120 : 150,
                        height: isMobile ? 120 : 150,
                        boxShadow: 3,
                        cursor: 'pointer',
                        '&:hover': {
                          animation: `${pulse} 1.5s infinite`,
                        }
                      }}
                      onClick={() => navigate('/profile/edit-photos')}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: isMobile ? 120 : 150,
                        height: isMobile ? 120 : 150,
                        bgcolor: theme.palette.primary.main,
                        fontSize: isMobile ? 40 : 60,
                        boxShadow: 3
                      }}
                    >
                      {userInfo?.firstName?.charAt(0) || 'U'}
                    </Avatar>
                  )}
                  <IconButton
                    className="edit-icon"
                    onClick={() => navigate('/profile/edit-photos')}
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      backgroundColor: 'background.paper',
                      borderRadius: '50%',
                      boxShadow: 3,
                      padding: 1,
                      opacity: 0.8,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'primary.main',
                        opacity: 1,
                        '& svg': {
                          color: 'white'
                        }
                      },
                    }}
                  >
                    <EditIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  </IconButton>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Email Verification Status */}
                <Box mt={3} mb={4}>
                  {emailVerified ? (
                    <Grow in={true}>
                      <Alert
                        severity="success"
                        icon={<VerifiedUser />}
                        sx={{
                          alignItems: 'center',
                          borderRadius: 2
                        }}
                      >
                        {t('profile.emailVerified', 'Email verified')}
                      </Alert>
                    </Grow>
                  ) : (
                    <Zoom in={true}>
                      <Box>
                        <Alert
                          severity="warning"
                          sx={{
                            mb: 2,
                            borderRadius: 2
                          }}
                        >
                          {t('profile.emailNotVerified', 'Email not verified')}
                        </Alert>
                        <Button
                          onClick={() => navigate('/verify-email')}
                          variant="contained"
                          fullWidth
                          sx={{
                            borderRadius: 2,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: theme.shadows[2],
                            '&:hover': {
                              boxShadow: theme.shadows[4],
                            }
                          }}
                        >
                          {t('profile.verifyEmailButton', 'Verify Email')}
                        </Button>
                      </Box>
                    </Zoom>
                  )}
                </Box>

                {/* Logout Button */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 3
                }}>
                  <Button
                    onClick={handleLogout}
                    variant="outlined"
                    color="error"
                    startIcon={<LogoutIcon />}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      fontSize: '1rem',
                      width: '100%',
                      maxWidth: 300,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: theme.shadows[1],
                      '&:hover': {
                        boxShadow: theme.shadows[3],
                        backgroundColor: 'error.light',
                        color: 'white'
                      },
                    }}
                  >
                    {t('profile.logoutButton', 'Logout')}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Fade>
        </Grid>
      </Grid >

      {/* Loading Backdrop */}
      < Backdrop
        open={loading || isLoggingOut
        }
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }}
      >
        <CircularProgress color="inherit" size={60} thickness={4} />
      </Backdrop >
    </>
  );
};

export default ProfilePage;